import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
    GET_AD_ASSETS,
    UPDATE_AD_ASSET_DETAILS,
    UPDATE_AD_ASSET_CROP_DATA,
    DELETE_AD_ASSET,
    UPDATE_AD_ASSET_POSITIONS,
} from '@/graphql/ads/ad-assets-queries'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

export const useCarouselCards = (campaignId, adId) => {
    const { currentStepName, getStepContentData, removeStepContentData } = useWizard()
    const [cards, setCards] = useState([])
    const [expandedCard, setExpandedCard] = useState('')
    const [isApplyingToAll, setIsApplyingToAll] = useState(false)
    const librarySource = getStepContentData('uploadAssets')?.source
    const isReplacingRef = useRef(false)

    const [updateAdAssetDetails] = useMutation(UPDATE_AD_ASSET_DETAILS)
    const [updateAdAssetCropData] = useMutation(UPDATE_AD_ASSET_CROP_DATA)
    const [deleteAdAsset] = useMutation(DELETE_AD_ASSET)
    const [updatePositions] = useMutation(UPDATE_AD_ASSET_POSITIONS)

    const {
        loading,
        data,
        refetch: refetchAssets,
        startPolling,
        stopPolling,
    } = useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId },
        skip: !campaignId || !adId,
        pollInterval: 0, // Start with no polling, will be enabled conditionally
    })

    // Refetch data whenever user navigates to the carouselCards step (skip if replace is pending)
    useEffect(() => {
        if (currentStepName !== 'carouselCards' || !campaignId || !adId) return
        if (getStepContentData('uploadAssets')?.replaceAssetId) return

        refetchAssets()
    }, [currentStepName, campaignId, adId, refetchAssets])

    // Replaces a carousel card: deletes the old asset, fetches the updated list

    const performCardReplace = async ({ replaceAssetId, replaceAssetPosition, existingCardIds }) => {
        isReplacingRef.current = true
        setCards((prev) => prev.filter((card) => card.id !== replaceAssetId))

        const knownIds = new Set(existingCardIds || [])

        try {
            await deleteAdAsset({ variables: { id: replaceAssetId } })

            const { data: refetchedData } = await refetchAssets()
            const assets = [...(refetchedData?.AdAsset?.adAssets || [])].sort(
                (a, b) => (a.position || 0) - (b.position || 0),
            )

            const oldCards = assets.filter((a) => knownIds.has(a.id))
            const newCards = assets.filter((a) => !knownIds.has(a.id))

            if (newCards.length > 0 && replaceAssetPosition != null) {
                const insertAt = Math.min(replaceAssetPosition, oldCards.length)
                oldCards.splice(insertAt, 0, ...newCards)

                // Show the correct order immediately while updatePositions persists to backend.
                setCards(
                    oldCards.map((asset, index) => ({
                        ...asset,
                        headline: asset.headline || '',
                        description: asset.description || '',
                        image_alt_text: asset.image_alt_text || '',
                        order: index,
                        updatingThumbnail: false,
                    })),
                )

                await updatePositions({
                    variables: {
                        updates: oldCards.map((card, index) => ({
                            id: card.id,
                            position: index,
                        })),
                    },
                })
            }
        } finally {
            isReplacingRef.current = false
            await refetchAssets()
        }
    }

    // Trigger card replacement when returning to carouselCards with a pending replaceAssetId
    useEffect(() => {
        if (currentStepName !== 'carouselCards') return

        const { replaceAssetId, replaceAssetPosition, existingCardIds } = getStepContentData('uploadAssets') || {}

        if (!replaceAssetId) return

        removeStepContentData('uploadAssets', 'replaceAssetId')
        removeStepContentData('uploadAssets', 'replaceAssetPosition')
        removeStepContentData('uploadAssets', 'existingCardIds')

        performCardReplace({ replaceAssetId, replaceAssetPosition, existingCardIds })
    }, [currentStepName])

    useEffect(() => {
        if (!data || isReplacingRef.current) return

        const assets = data.AdAsset.adAssets
        const assetsWithFormData = [...assets]
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map((asset, index) => ({
                ...asset,
                headline: asset.headline || '',
                description: asset.description || '',
                image_alt_text: asset.image_alt_text || '',
                order: index,
                updatingThumbnail: false,
            }))

        setCards(assetsWithFormData)
        // Check if any video assets are generating thumbnails
        const hasGeneratingVideos = assets.some(
            (asset) => asset.type === 'video' && asset.status === 'generating_frames',
        )

        if (hasGeneratingVideos) startPolling(2000)
        else stopPolling()
    }, [data, startPolling, stopPolling, updateAdAssetCropData])

    // Cleanup polling on unmount
    useEffect(() => {
        return () => stopPolling()
    }, [stopPolling])

    const handleInputChange = async (cardId, field, value) => {
        setCards((prev) => prev.map((card) => (card.id === cardId ? { ...card, [field]: value } : card)))
        const asset = cards.find((card) => card.id === cardId)
        mutateAssetDetails(asset, field, value)
    }

    const mutateAssetDetails = async (asset, field, value) => {
        const data = {
            id: asset.id,
            __typename: 'AdAsset',
            headline: asset.headline,
            description: asset.description,
            image_alt_text: asset.image_alt_text,
            [field]: value,
        }

        return await updateAdAssetDetails({
            variables: { assetId: asset.id, [field]: value },
            optimisticResponse: {
                __typename: 'Mutation',
                updateAdAssetDetails: data,
            },
        })
    }

    const handleApplyToAllCards = async (sourceCardId, field) => {
        if (!sourceCardId) return

        const sourceCard = cards.find((card) => card.id === sourceCardId)
        if (!sourceCard?.[field]) return

        setIsApplyingToAll(true)
        try {
            const otherCards = cards.filter((c) => c.id !== sourceCardId)

            // Optimistically update local state
            setCards(cards.map((card) => (card.id !== sourceCardId ? { ...card, [field]: sourceCard[field] } : card)))

            // Perform mutations
            await Promise.all(
                otherCards.map(async (otherCard) => {
                    await mutateAssetDetails(otherCard, field, sourceCard[field])
                }),
            )
        } finally {
            setIsApplyingToAll(false)
        }
    }

    const removeCardFromState = (cardId) => {
        setCards((prev) => prev.filter((card) => card.id !== cardId))
        if (expandedCard === cardId) setExpandedCard(null)
    }

    return {
        cards,
        setCards,
        expandedCard,
        setExpandedCard,
        isApplyingToAll,
        loading,
        data,
        refetchAssets,
        handleInputChange,
        handleApplyToAllCards,
        removeCardFromState,
    }
}
